import { appConfigStore } from 'Models/AppConfigStore';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { PassengerBookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerBookingWizardData';
import { WizardErrors } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { checkPassengersAndDriver } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/Sidebar/FerryFilters/PassengerDetails/PassengerTypeInputs';
import If from 'Views/Components/If/If';
import TrailerCheckbox from './TrailerCheckbox/TrailerCheckbox';
import { UpdateWizardData } from './FerryFilterPassengers';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { useBookingWizardDataStore } from '../../../BookingWizardDataStore';
import { VehicleBookingWizardData } from '../../../BookingWizardData/VehicleBookingWizardData';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export interface FerryFilterSubheadingProps {
	onPassengerTab: boolean;
	onVehicleTab: boolean;
	passengerButtonSubheading: string;
	vehicleButtonSubheading: string;
	trailersEnabled: boolean;
	passengerCount: number;
}

export function FerryFilterSubheading({
	onPassengerTab,
	onVehicleTab,
	passengerButtonSubheading,
	vehicleButtonSubheading,
	trailersEnabled,
	passengerCount,
}: FerryFilterSubheadingProps) {
	const { wizardData, setWizardData, oldWizardData, innerWizardErrors } = useBookingWizardDataStore();

	const trailerCheckboxSelected =
		wizardData.innerWizardData instanceof VehicleBookingWizardData
			? wizardData.innerWizardData.trailerCheckboxSelected
			: false;

	const { hasPassengers, hasDriver, requiresPassengers } = checkPassengersAndDriver(wizardData);
	const { hasPassengers: oldWizardDataHasPassengers } = checkPassengersAndDriver(oldWizardData ?? undefined);

	const needsDriver = hasPassengers && !hasDriver && wizardData.innerWizardData.type === 'vehicle';

	const typedErrors = innerWizardErrors as WizardErrors<PassengerBookingWizardData | VehicleBookingWizardData>;

	return (
		<>
			{onPassengerTab &&
				stringNotEmpty(passengerButtonSubheading) &&
				passengerButtonSubheading !== 'NO_LABEL' && (
					<div className="category-title-container">
						<p>{passengerButtonSubheading}</p>
					</div>
				)}

			{onVehicleTab && stringNotEmpty(vehicleButtonSubheading) && vehicleButtonSubheading !== 'NO_LABEL' && (
				<>
					<div className="category-title-container">
						<p>{vehicleButtonSubheading}</p>
					</div>
					<If condition={trailersEnabled}>
						<TrailerCheckbox
							hasTrailer={trailerCheckboxSelected}
							setHasTrailer={() => handleTrailerCheckbox(wizardData, setWizardData)}
						/>
					</If>
					<If condition={!appConfigStore.vehicleBookingRequiresAdult && !hasPassengers}>
						<MessageBox status="warning" slim errorShake={isNotNullOrUndefined(typedErrors.adultTickets)}>
							{wizardData.wizardMode === 'ALTERATION' &&
							!oldWizardDataHasPassengers &&
							appConfigStore.DriverlessVehicleBookingPriceIncludesAdultPrice ? (
								<p>
									<FormattedMessage
										id="driverless-vehicle__alteration-warning-message"
										defaultMessage="You have booked a driverless vehicle and cannot add passengers to this booking. Please cancel and create a new booking."
									/>
								</p>
							) : (
								<p>
									<FormattedMessage
										id="driverless-vehicle__warning-message"
										defaultMessage="You are booking a driverless vehicle"
									/>
								</p>
							)}
						</MessageBox>
					</If>
					<If condition={needsDriver}>
						<MessageBox status="warning" slim errorShake={isNotNullOrUndefined(typedErrors.adultTickets)}>
							{
								<p>
									<FormattedMessage
										id="no-driver-selected__warning-message"
										defaultMessage="A driver is required on a vehicle booking"
									/>
								</p>
							}
						</MessageBox>
					</If>
				</>
			)}
			<If
				condition={
					wizardData.tabSelected !== 'freight' &&
					(appConfigStore.vehicleBookingRequiresAdult || requiresPassengers) &&
					!hasPassengers
				}
			>
				<MessageBox status="error" slim errorShake={isNotNullOrUndefined(typedErrors.adultTickets)}>
					{
						<p>
							<FormattedMessage
								id="minimum-passengers__error-message"
								defaultMessage="Minimum of 1 passenger is required"
							/>
						</p>
					}
				</MessageBox>
			</If>
		</>
	);
}

function handleTrailerCheckbox(wizardData: BookingWizardData, onUpdateFilters: UpdateWizardData) {
	const newData = new BookingWizardData({ ...wizardData });
	if (newData.innerWizardData instanceof VehicleBookingWizardData) {
		newData.innerWizardData.trailerCheckboxSelected = !newData.innerWizardData.trailerCheckboxSelected;

		if (wizardData.wizardMode === 'CREATE' && !newData.innerWizardData.trailerCheckboxSelected) {
			// #trailer-checkbox-deselect-on-no-trailer
			newData.innerWizardData.trailerTypeId = 'NO_TRAILER';
			newData.innerWizardData.trailerLengthId = '';
		}
	}
	onUpdateFilters(newData);
}
